/**
 * ActivityThreadCardTooltip — the Activity card's hover details panel.
 *
 * A direct port of t3code's `SidebarThreadTooltip` (`Sidebar.tsx`). The whole
 * card is the trigger, and the panel answers the questions the card had to
 * truncate away:
 *
 *   ┌────────────────────────────────────┐
 *   │ Match T3 Code Project Dropdown     │  ← the untruncated title
 *   │ ▣ Forge                            │  ← project
 *   │ ▤ Alexander's MacBook Pro          │  ← where it runs (☁ Cloud on a Sprite)
 *   │ ⑂ t3code/fix-project-dropdown      │  ← branch
 *   │ ✳ Claude Opus 5                    │  ← who is doing the work
 *   └────────────────────────────────────┘
 *
 * The markup below is upstream's, row for row and class for class, rather than
 * a tidier arrangement that renders the same thing: this panel exists to look
 * identical to t3code's, and a refactor is the thing that quietly drifts from
 * it the next time either side changes.
 *
 * **Four substitutions, each because Forge has no equivalent to copy.**
 *
 * 1. `ProviderInstanceIcon` → `PROVIDER_SETTINGS_ICON_BY_PROVIDER`. t3code
 *    models *provider instances* (two Claude accounts, each with its own accent
 *    colour and badge); Forge models providers. There is no instance to badge,
 *    so the row is upstream's `modelLabel`-only branch — the one it takes when
 *    a single instance makes the badge meaningless.
 * 2. The model is read from the store here rather than passed in.
 *    `SidebarThreadSummary` carries no model — only the full `ThreadShell` does
 *    — and subscribing every card in the sidebar to the shell index to fill a
 *    tooltip nobody has hovered yet would be a subscription per row for
 *    nothing. Base UI mounts a tooltip's portal only while it is open, so this
 *    component (and its subscription) exists only for the card under the
 *    cursor.
 * 3. The "where it runs" row collapses CMMD Sprites to `☁ Cloud`. See
 *    `resolveActivityCardEnvironmentRow`; upstream has no cloud runtime, so
 *    there is nothing to copy and this follows Forge's own Projects layout.
 * 4. `z-[140]` on the positioner is NOT taken. Forge puts its whole floating
 *    stack — dialogs, menus, popovers, tooltips — on `z-50`; upstream's 140 is
 *    a position in *its* layer scale, and importing the number would raise
 *    tooltips above Forge's dialogs rather than reproduce upstream's look.
 */

import { CircleAlertIcon, CloudIcon, GitBranchIcon, ServerIcon, TerminalIcon } from "lucide-react";

import { ProjectIcon } from "../ProjectIcon";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import { TooltipPopup } from "../ui/tooltip";
import { PROVIDER_SETTINGS_ICON_BY_PROVIDER } from "../providerSettingsIcons";
import { cn } from "~/lib/utils";
import { selectEnvironmentState, useStore } from "../../store";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime/catalog";
import type { SidebarThreadSummary } from "../../types";
import type { TerminalStatusIndicator } from "./SidebarThreadRow.helpers";
import {
  resolveActivityCardEnvironmentRow,
  resolveThreadModelLabel,
  terminalProcessLabel,
} from "./activityThreadCardTooltip.logic";

export interface ActivityThreadCardTooltipProps {
  readonly thread: SidebarThreadSummary;
  readonly projectTitle: string | null;
  readonly projectCwd: string | null;
  /** The machine the thread runs on ("This Device", a cloud VM, a linked host). */
  readonly environmentLabel: string | null;
  /**
   * The branch the card's third line shows. Passed in rather than re-read from
   * `thread.branch` so the panel and the card cannot name different branches
   * for one thread — see `resolveDisplayedThreadBranch`.
   */
  readonly displayBranch: string | null;
  readonly branchMismatch: { readonly threadBranch: string; readonly currentBranch: string } | null;
  readonly terminalStatus: TerminalStatusIndicator | null;
  readonly terminalProcessCount: number;
}

/**
 * Upstream's provider row, reduced to its single-instance branch.
 *
 * Kept as its own component only because it is the one row that subscribes —
 * a hook cannot live inside the conditional the other rows are written as.
 */
function ActivityThreadCardModelRow(props: { readonly thread: SidebarThreadSummary }) {
  const { thread } = props;
  const modelSelection = useStore(
    (state) =>
      selectEnvironmentState(state, thread.environmentId).threadShellById[thread.id]
        ?.modelSelection ?? null,
  );
  const modelLabel = resolveThreadModelLabel(modelSelection);
  // Upstream drops this row when the thread has no driver at all. It never
  // drops a row that HAS a model, so a provider with no registered glyph keeps
  // its name and falls back to a neutral dot — the same trade the settings and
  // onboarding provider marks already make.
  if (modelSelection === null || modelLabel === null) return null;
  const ProviderIcon = PROVIDER_SETTINGS_ICON_BY_PROVIDER[modelSelection.provider];
  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* Initials would swallow a size-3 glyph: the mark desaturates and the
          name carries the identity — upstream's rule for this row. */}
      {ProviderIcon ? (
        <ProviderIcon aria-hidden className="size-3 shrink-0 grayscale opacity-60" />
      ) : (
        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-muted-foreground" />
      )}
      <div className="min-w-0 truncate text-foreground/75">{modelLabel}</div>
    </div>
  );
}

export function ActivityThreadCardTooltip({
  thread,
  projectTitle,
  projectCwd,
  environmentLabel,
  displayBranch,
  branchMismatch,
  terminalStatus,
  terminalProcessCount,
}: ActivityThreadCardTooltipProps) {
  const resolveProjectIcon = useProjectIconResolver();
  const savedEnvironments = useSavedEnvironmentRegistryStore((state) => state.byId);
  const environmentRow = resolveActivityCardEnvironmentRow({
    environmentId: thread.environmentId,
    environmentLabel,
    savedEnvironments,
  });
  return (
    <TooltipPopup
      side="right"
      align="start"
      sideOffset={4}
      variant="glass"
      className="max-w-80 text-left whitespace-normal [&_[data-slot=tooltip-viewport]]:p-0"
      data-testid={`thread-details-tooltip-${thread.id}`}
    >
      <div className="flex max-w-80 min-w-0 flex-col gap-2 p-[var(--floating-content-inset)]">
        <div className="min-w-0 truncate text-xs leading-none font-medium text-foreground">
          {thread.title}
        </div>
        <div className="grid gap-1.5 pl-0.5 text-xs text-muted-foreground">
          {projectTitle ? (
            <div className="flex min-w-0 items-center gap-2">
              <ProjectIcon
                environmentId={thread.environmentId}
                cwd={projectCwd ?? ""}
                projectName={projectTitle}
                canonicalIdentity={resolveProjectIcon(thread.environmentId, thread.projectId)}
                className="size-3 shrink-0 stroke-muted-foreground"
              />
              <div className="min-w-0 truncate text-foreground/75">{projectTitle}</div>
            </div>
          ) : null}
          {environmentRow ? (
            <div className="flex min-w-0 items-center gap-2">
              {/* Cloud gets the cloud glyph and the word "Cloud"; a real machine
                  keeps the server glyph and its own name. Same pairing the
                  Projects layout and `environmentKind.ts` already use. */}
              {environmentRow.isCloud ? (
                <CloudIcon className="size-3 shrink-0 stroke-muted-foreground" />
              ) : (
                <ServerIcon className="size-3 shrink-0 stroke-muted-foreground" />
              )}
              <div className="min-w-0 truncate text-foreground/75">{environmentRow.label}</div>
            </div>
          ) : null}
          {displayBranch ? (
            <div className="flex min-w-0 items-center gap-2">
              <GitBranchIcon className="size-3 shrink-0 stroke-muted-foreground" />
              <div className="min-w-0 truncate text-foreground/75">{displayBranch}</div>
            </div>
          ) : null}
          {branchMismatch ? (
            <div className="flex min-w-0 items-start gap-2 text-warning">
              <CircleAlertIcon aria-hidden className="mt-0.5 size-3 shrink-0 stroke-current" />
              <div className="min-w-0 flex-1 leading-5 wrap-break-word">
                You&apos;re currently checked out on another branch.
              </div>
            </div>
          ) : null}
          <ActivityThreadCardModelRow thread={thread} />
          {terminalStatus ? (
            <div className="flex min-w-0 items-center gap-2">
              <TerminalIcon
                aria-hidden
                className={cn("size-3 shrink-0", terminalStatus.colorClass)}
              />
              <div className="min-w-0 truncate text-foreground/75">
                {terminalProcessLabel(terminalProcessCount)}
              </div>
            </div>
          ) : null}
          {thread.session?.lastError ? (
            <div className="flex min-w-0 items-center gap-2 text-destructive-foreground">
              <CircleAlertIcon className="size-3 shrink-0 stroke-current" />
              <div className="min-w-0 truncate">Error occurred</div>
            </div>
          ) : null}
        </div>
      </div>
    </TooltipPopup>
  );
}
